import {
    forwardRef,
    HTMLAttributeAnchorTarget,
    memo,
    MouseEventHandler,
    PropsWithChildren,
    useCallback,
    useMemo,
} from 'react';
import {useRouter} from 'next/router';
import {TPropsWithClassName} from 'core/types';
import classNames from 'classnames';
import NextLink, {LinkProps} from 'components/CustomRoutingLink';
import {ITypographyPublicProps, TTypographyVariant, Typography} from 'components/Typography';
import style from './style.module.scss';
import {Arrow} from 'components/icons/Arrow';

type TBaseProps = Omit<LinkProps, 'onClick'>;

export const LINK_THEME = {
    black: 'black',
    red: 'red',
} as const;

export const LINK_SIZE = {
    large: 'large',
    medium: 'medium',
    small: 'small',
} as const;

interface IProps extends Partial<TBaseProps> {
    isForward?: boolean;
    isBackward?: boolean;
    isUnderlining?: boolean;
    onClick?: (arg: any) => void;
    disabled?: boolean;
    text?: string;
    theme?: keyof typeof LINK_THEME;
    size?: keyof typeof LINK_SIZE;
    variant?: TTypographyVariant;
    target?: HTMLAttributeAnchorTarget;
    title?: string;
    element?: ITypographyPublicProps['element'];
}

const textVariant = {
    [LINK_SIZE.large]: 'p',
    [LINK_SIZE.medium]: 'p-medium',
    [LINK_SIZE.small]: 'p-small',
} as const;

interface ILinkItemProps extends Pick<IProps, 'isForward' | 'isBackward' | 'text' | 'size' | 'element' | 'variant'> {}

export const LinkItem = memo<ILinkItemProps>(
    ({isBackward, variant, element, isForward, text, size = LINK_SIZE.large}) => {
        return (
            <span
                className={classNames(style.linkWithIcons, {
                    [style.isReverse]: isForward,
                })}
            >
                {(isBackward || isForward) && (
                    <Arrow
                        className={classNames(style.arrowIcon, isBackward ? style.backIcon : style.forwardIcon)}
                        direction={isBackward ? 'left' : 'right'}
                        size={'sm'}
                        type={'line'}
                    />
                )}
                <Typography
                    className={classNames(style.text, 'link__text')}
                    element={element}
                    variant={variant || textVariant[size]}
                >
                    {text}
                </Typography>
            </span>
        );
    },
);

const Link = memo(
    forwardRef<HTMLElement, TPropsWithClassName<PropsWithChildren<IProps>>>(function Link(
        {
            isForward = false,
            isBackward = false,
            children,
            theme = LINK_THEME.red,
            href = '',
            className,
            onClick,
            disabled = false,
            variant,
            isUnderlining = false,
            size = LINK_SIZE.large,
            target,
            text,
            shallow,
            element = 'span',
            title = '',
            ...restLinkAttributes
        },
        ref,
    ) {
        const router = useRouter();

        const handleClick: MouseEventHandler<HTMLSpanElement> = useCallback(
            (event) => {
                if (!disabled) {
                    onClick?.(event);
                    if (isBackward && !Boolean(href)) {
                        router.back();
                    }
                }
            },
            [disabled, onClick, isBackward, href, router],
        );

        const linkClassNames = useMemo(
            () =>
                classNames(className, style.link, {
                    [style.forwardLink]: isForward,
                    [style.backLink]: isBackward,
                    [style.disabledLink]: disabled,
                    [style.underlining]: isUnderlining,
                    [style.blackLink]: theme === LINK_THEME.black,
                    [style.redLink]: theme === LINK_THEME.red,
                    [style.buttonLink]: disabled,
                }),
            [className, isForward, isBackward, disabled, theme, isUnderlining],
        );

        if (target) {
            return (
                <NextLink
                    {...restLinkAttributes}
                    className={linkClassNames}
                    href={href}
                    passHref={true}
                    ref={ref as any}
                    rel={'noopener noreferrer'}
                    target={target}
                    title={title}
                    onClick={handleClick}
                >
                    {children ?? (
                        <LinkItem
                            element={element}
                            isBackward={isBackward}
                            isForward={isForward}
                            size={size}
                            text={text}
                            variant={variant}
                        />
                    )}
                </NextLink>
            );
        }

        return !disabled && (Boolean(href) || isBackward) ? (
            <NextLink {...restLinkAttributes} href={href} legacyBehavior={true} shallow={shallow}>
                <a className={linkClassNames} onClick={handleClick} ref={ref as any} title={title}>
                    {children ?? (
                        <LinkItem
                            element={element}
                            isBackward={isBackward}
                            isForward={isForward}
                            size={size}
                            text={text}
                            variant={variant}
                        />
                    )}
                </a>
            </NextLink>
        ) : (
            <button className={linkClassNames} ref={ref as any}>
                {children ?? (
                    <LinkItem
                        element={element}
                        isBackward={isBackward}
                        isForward={isForward}
                        size={size}
                        text={text}
                        variant={variant}
                    />
                )}
            </button>
        );
    }),
);

export default Link;
